// Hooks de React Query para o domínio CRM Proposal (Story 11.6).
// - useCrmProposalsInfinite — listagem paginada por cursor (DR-032) + filtros.
// - useCrmProposal — detalhe.
// - useCreateCrmProposal / useUpdateCrmProposal.
//
// Convenção: queryKey ['crm-proposals', 'list', filters] e ['crm-proposals', 'detail', id].
// Após qualquer mutation: invalida lists + detail afetado.

import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
} from '@tanstack/react-query';

import {
  acceptCrmProposal,
  convertCrmProposal,
  createCrmProposal,
  duplicateCrmProposal,
  fetchCrmProposal,
  fetchCrmProposals,
  fetchPublicProposal,
  rejectCrmProposal,
  sendCrmProposal,
  updateCrmProposal,
  type ConvertProposalResult,
  type CrmProposalDetailEntity,
  type CrmProposalEntity,
  type CrmProposalListParams,
  type CrmProposalListResponse,
} from '../lib/crm-proposals-api';
import type {
  ConvertCrmProposalDto,
  CreateCrmProposalDto,
  ProposalPublicView,
  UpdateCrmProposalDto,
} from '@control-center/shared';

const DEFAULT_LIMIT = 20;

export const crmProposalsKeys = {
  all: ['crm-proposals'] as const,
  lists: () => [...crmProposalsKeys.all, 'list'] as const,
  list: (params: Omit<CrmProposalListParams, 'cursor'>) =>
    [...crmProposalsKeys.lists(), params] as const,
  details: () => [...crmProposalsKeys.all, 'detail'] as const,
  detail: (id: string) => [...crmProposalsKeys.details(), id] as const,
};

export type CrmProposalsListFilters = Omit<CrmProposalListParams, 'cursor' | 'limit'>;

export function useCrmProposalsInfinite(filters: CrmProposalsListFilters = {}) {
  return useInfiniteQuery<
    CrmProposalListResponse,
    Error,
    InfiniteData<CrmProposalListResponse>,
    ReturnType<typeof crmProposalsKeys.list>,
    string | undefined
  >({
    queryKey: crmProposalsKeys.list({ ...filters, limit: DEFAULT_LIMIT }),
    queryFn: ({ pageParam, signal }) =>
      fetchCrmProposals({ ...filters, limit: DEFAULT_LIMIT, cursor: pageParam }, signal),
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage.meta.next_cursor ?? undefined,
    staleTime: 30_000,
  });
}

export function useCrmProposal(id: string | undefined) {
  return useQuery<CrmProposalDetailEntity, Error>({
    queryKey: id ? crmProposalsKeys.detail(id) : ['crm-proposals', 'detail', 'noop'],
    queryFn: ({ signal }) => fetchCrmProposal(id as string, signal),
    enabled: Boolean(id),
    staleTime: 30_000,
  });
}

export function useCreateCrmProposal() {
  const qc = useQueryClient();
  return useMutation<CrmProposalEntity, Error, CreateCrmProposalDto>({
    mutationFn: (payload) => createCrmProposal(payload),
    onSuccess: (proposal) => {
      qc.invalidateQueries({ queryKey: crmProposalsKeys.lists() });
      qc.invalidateQueries({ queryKey: crmProposalsKeys.detail(proposal.id) });
    },
  });
}

export interface UpdateCrmProposalArgs {
  id: string;
  payload: UpdateCrmProposalDto;
}

export function useUpdateCrmProposal() {
  const qc = useQueryClient();
  return useMutation<CrmProposalEntity, Error, UpdateCrmProposalArgs>({
    mutationFn: ({ id, payload }) => updateCrmProposal(id, payload),
    onSuccess: (proposal) => {
      qc.invalidateQueries({ queryKey: crmProposalsKeys.lists() });
      qc.invalidateQueries({ queryKey: crmProposalsKeys.detail(proposal.id) });
    },
  });
}

// -----------------------------------------------------------------------------
// Ações de ciclo (Story 11.7). Após cada uma: invalida lists + o detalhe afetado.
// -----------------------------------------------------------------------------

/** Invalida lists + detalhe da proposta — usado por todas as ações de ciclo. */
function invalidateProposal(
  qc: ReturnType<typeof useQueryClient>,
  proposal: CrmProposalEntity,
): void {
  qc.invalidateQueries({ queryKey: crmProposalsKeys.lists() });
  qc.invalidateQueries({ queryKey: crmProposalsKeys.detail(proposal.id) });
}

export function useSendCrmProposal() {
  const qc = useQueryClient();
  return useMutation<CrmProposalEntity, Error, string>({
    mutationFn: (id) => sendCrmProposal(id),
    onSuccess: (proposal) => invalidateProposal(qc, proposal),
  });
}

export function useAcceptCrmProposal() {
  const qc = useQueryClient();
  return useMutation<CrmProposalEntity, Error, string>({
    mutationFn: (id) => acceptCrmProposal(id),
    onSuccess: (proposal) => invalidateProposal(qc, proposal),
  });
}

export interface RejectCrmProposalArgs {
  id: string;
  rejectionReason: string;
}

export function useRejectCrmProposal() {
  const qc = useQueryClient();
  return useMutation<CrmProposalEntity, Error, RejectCrmProposalArgs>({
    mutationFn: ({ id, rejectionReason }) => rejectCrmProposal(id, rejectionReason),
    onSuccess: (proposal) => invalidateProposal(qc, proposal),
  });
}

export function useDuplicateCrmProposal() {
  const qc = useQueryClient();
  return useMutation<CrmProposalEntity, Error, string>({
    mutationFn: (id) => duplicateCrmProposal(id),
    onSuccess: (proposal) => {
      // A duplicata é uma NOVA proposta (novo id) — invalida só as lists.
      qc.invalidateQueries({ queryKey: crmProposalsKeys.lists() });
      qc.invalidateQueries({ queryKey: crmProposalsKeys.detail(proposal.id) });
    },
  });
}

export interface ConvertCrmProposalArgs {
  id: string;
  payload?: ConvertCrmProposalDto;
}

/**
 * Converte a proposta aceita em assinatura (Story 11.9). Após sucesso, invalida o
 * detalhe/lists de propostas E as queries de assinaturas (uma nova foi criada).
 */
export function useConvertCrmProposal() {
  const qc = useQueryClient();
  return useMutation<ConvertProposalResult, Error, ConvertCrmProposalArgs>({
    mutationFn: ({ id, payload }) => convertCrmProposal(id, payload),
    onSuccess: (_result, { id }) => {
      qc.invalidateQueries({ queryKey: crmProposalsKeys.lists() });
      qc.invalidateQueries({ queryKey: crmProposalsKeys.detail(id) });
      // Assinatura criada → invalida o domínio de subscriptions (lists + details).
      qc.invalidateQueries({ queryKey: ['subscriptions'] });
    },
  });
}

// -----------------------------------------------------------------------------
// Link público read-only (Story 11.7 AC2) — sem auth, sem retry em 404/410/429.
// -----------------------------------------------------------------------------

export function usePublicProposal(token: string | undefined) {
  return useQuery<ProposalPublicView, Error>({
    queryKey: ['public-proposal', token],
    queryFn: ({ signal }) => fetchPublicProposal(token as string, signal),
    enabled: Boolean(token),
    retry: false,
    staleTime: 60_000,
  });
}
